import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import { Toastify } from "../../helper/Constant";

const SignPolicyModal = ({ show, closeModal, policyId, policyIds, onSignSuccess, signPolicy, bulkSignPolicy, isBulk }) => {
    const sigPad = useRef({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingText, setLoadingText] = useState('Submitting...');

    const loadingMessages = [
        "Submitting...",
        "Extracting Document...",
        "Pasting Signature...",
        "Generating Secured PDF...",
        "Encrypting...",
        "Uploading to Cloud..."
    ];

    useEffect(() => {
        let interval;
        if (loading) {
            setLoadingText(loadingMessages[0]);
            interval = setInterval(() => {
                setLoadingText(prev => {
                    const available = loadingMessages.filter(m => m !== prev);
                    return available[Math.floor(Math.random() * available.length)];
                });
            }, 2000);
        }
        return () => clearInterval(interval);
    }, [loading]);

    const clear = () => {
        sigPad.current.clear();
        setError('');
    };

    const save = () => {
        setError('');
        if (sigPad.current.isEmpty()) {
            setError('Please provide a signature.');
            return;
        }

        setLoading(true);
        const signatureImage = sigPad.current.getTrimmedCanvas().toDataURL('image/png');

        if (isBulk && bulkSignPolicy) {
            // Bulk Signing Flow
            bulkSignPolicy({ policyIds, signatureImage })
                .then(res => {
                    setLoading(false);
                    if (res.data.statusCode === 1) {
                        closeModal();
                        onSignSuccess(res.data.message);
                    } else {
                        const errorMsg = res.data.message || 'Failed to sign policies.';
                        setError(errorMsg);
                    }
                })
                .catch(err => {
                    setLoading(false);
                    setError('Failed to sign policies. Please try again.');
                    console.error(err);
                });
        } else {
            // Single Policy Signing Flow (Original)
            // Support both old prop (policyId) and new prop (policyIds array with 1 item)
            const targetId = policyId || (policyIds && policyIds[0]);

            signPolicy({ policyId: targetId, signatureImage })
                .then(res => {
                    setLoading(false);
                    if (res.data.statusCode === 1) {
                        closeModal();
                        onSignSuccess(res.data.message);
                    } else {
                        const errorMsg = res.data.message ||
                            (res.data.error && res.data.error.responseMessage) ||
                            (res.data.error && res.data.error.errorMessage) ||
                            'Failed to sign policy.';
                        setError(errorMsg);
                        console.error("Sign policy failed:", res.data);
                    }
                })
                .catch(err => {
                    setLoading(false);
                    setError('Failed to sign policy. Please try again.');
                    console.error(err);
                });
        }
    };

    return (
        <Modal
            show={show}
            onHide={loading ? null : closeModal}
            centered
            backdrop={loading ? 'static' : true}
            keyboard={!loading}
        >
            <Modal.Header closeButton={!loading} className="bg-light border-0">
                <Modal.Title className="w-100 text-center text-primary font-weight-bold">
                    <i className="fa fa-pencil-square-o mr-2"></i>
                    {isBulk ? `Sign ${policyIds ? policyIds.length : ''} Pending Policies` : 'Sign Policy Document'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-4">
                {loading && (
                    <div className="alert alert-warning text-center mb-4 shadow-sm" role="alert">
                        <div className="d-flex align-items-center justify-content-center mb-1">
                            <i className="fa fa-exclamation-triangle mr-2 text-warning" style={{ fontSize: '1.2rem' }}></i>
                            <h6 className="alert-heading mb-0 font-weight-bold text-dark">Processing Signature</h6>
                        </div>
                        <p className="mb-0 small text-muted">
                            Please do <strong>NOT</strong> close or refresh this window.
                            <br />
                            We are securely generating your signed document{isBulk ? 's' : ''}.
                        </p>
                    </div>
                )}

                <div className="text-center mb-2">
                    <p className="text-muted small text-uppercase font-weight-bold" style={{ letterSpacing: '1px' }}>
                        Draw your signature below
                    </p>
                </div>

                <div
                    className="signature-container mx-auto mb-3"
                    style={{
                        border: '2px dashed #ced4da',
                        borderRadius: '12px',
                        backgroundColor: '#f8f9fa',
                        padding: '4px',
                        position: 'relative'
                    }}
                >
                    <SignatureCanvas
                        penColor="black"
                        canvasProps={{ width: 455, height: 200, className: 'sigCanvas d-block mx-auto rounded' }}
                        ref={sigPad}
                        backgroundColor="transparent"
                    />
                    <div className="text-right pr-2">
                        <small className="text-muted font-italic" style={{ fontSize: '10px' }}>Secure Digital Signature</small>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger text-center mt-3 py-2 small font-weight-bold">
                        <i className="fa fa-times-circle mr-1"></i> {error}
                    </div>
                )}

                <p className="text-muted text-center small mt-3 px-3">
                    By clicking "Submit Signature", you acknowledge that you have read and understood {isBulk ? 'these policies' : 'this policy'} and agree to be legally bound by this digital signature.
                </p>
            </Modal.Body>
            <Modal.Footer className="border-0 justify-content-center pb-4 pt-0">
                <Button
                    variant="outline-secondary"
                    onClick={clear}
                    disabled={loading}
                    className="rounded-pill px-4 mr-2"
                >
                    Reset
                </Button>
                <Button
                    variant="primary"
                    onClick={save}
                    disabled={loading}
                    className="rounded-pill px-4 shadow-sm"
                    style={{ minWidth: '180px' }}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                            {loadingText}
                        </>
                    ) : (
                        <>Submit Signature</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SignPolicyModal;
